import { useEffect, useRef, useState } from "react";
import { adminRequest, operationKey } from "lib/adminSiteApi";
import { adminSignInAge, adminSignInMethod, confirmAdminSignIn } from "lib/adminAuth";
import { ActionForm, ErrorNotice, Fields, Loading, useRemote } from "components/admin/workspace/Controls";
import { useConfirm } from "components/ios/Alert";

// Owner-only shop settings (website launch L1): company profile, owner alerts, Cal.com links, legal texts and the
// Stripe connection. The server refuses everyone else; these panels are shown only to the owner.
const line = (max) => ({ type: "string", maxLength: max });
const req = (max) => ({ ...line(max), minLength: 1 });
const when = (at) => (at ? new Date(at).toLocaleString() : "");
const FRESH_MS = 4.5 * 60 * 1000;

const COMPANY = { type: "object", properties: {
  legalName: req(200),
  address: { type: "object", properties: { line1: req(200), line2: line(200), city: req(100), region: line(100), postalCode: line(20), country: req(100) },
    required: ["line1", "city", "country"] },
  contactEmail: { type: "string", format: "email", maxLength: 254 },
  contactPhone: { ...req(30), pattern: "^\\+?[0-9][0-9 ().-]{6,24}$" },
  taxId: line(50),
}, required: ["legalName", "address", "contactEmail", "contactPhone"] };
const COMPANY_COPY = { legalName: { label: "Legal company name" }, contactEmail: { label: "Contact email" },
  address: { label: "Registered address", fields: { line1: { label: "Street address" }, line2: { label: "Suite or unit (optional)" }, city: { label: "City" },
    region: { label: "State or region" }, postalCode: { label: "ZIP or postal code" }, country: { label: "Country" } } },
  contactPhone: { label: "Contact phone", help: "For example +1 555 0100." }, taxId: { label: "Tax ID (optional)" } };
const ALERTS = { type: "object", properties: {
  alertEmails: { type: "array", items: { type: "string", format: "email", maxLength: 254 } },
  senderName: req(100), senderEmail: { type: "string", format: "email", maxLength: 254 },
}, required: ["alertEmails", "senderName", "senderEmail"] };
const ALERTS_COPY = { alertEmails: { label: "Alert address" }, senderName: { label: "Sender name" },
  senderEmail: { label: "Sender address", help: "Your email provider must allow sending from this address. Check it before going live." } };
const BOOKING = { type: "object", properties: { reviewMeetingUrl: req(500), followUpMeetingUrl: req(500) }, required: ["reviewMeetingUrl", "followUpMeetingUrl"] };
const BOOKING_COPY = { reviewMeetingUrl: { label: "Review meeting (1 hour) booking link", help: "The https link of the Cal.com event. Notice periods are set in Cal.com." },
  followUpMeetingUrl: { label: "Follow-up (3 hours) booking link" } };

function SettingPanel({ id, title, description, schema, copy }) {
  const [tick, setTick] = useState(0), state = useRemote(`/admin/owner-settings/${id}`, tick);
  return <Loading state={state}>{state.data && <ActionForm key={`${id}:${state.data.revision}`} title={title} description={description} schema={schema}
    initial={{ revision: state.data.revision, ...(state.data.value || {}) }}
    renderFields={(value, setValue) => <Fields schema={schema} value={value} onChange={setValue} prefix={`owner-${id}`} fieldCopy={copy} />}
    submit={({ revision: _ignored, ...value }, key) => adminRequest(`/admin/owner-settings/${id}`, { method: "PUT", key, body: { revision: state.data.revision, value } })}
    onDone={() => setTick((n) => n + 1)}>
    {state.data.updatedAt && <p className="aw-muted">Last saved {when(state.data.updatedAt)}.</p>}
  </ActionForm>}</Loading>;
}

// Confirms the owner's own sign-in (Google window or password) before a secret is changed.
export function SignInAgain({ onDone }) {
  const [password, setPassword] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState(null);
  const google = adminSignInMethod() === "google";
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await confirmAdminSignIn(google ? undefined : password); setPassword(""); onDone(); }
    catch { setError(new Error("That did not work. Check your password or try again.")); }
    finally { setBusy(false); }
  };
  return <form className="aw-notice" onSubmit={confirm} aria-label="Confirm your sign-in">
    <strong>Confirm it is you</strong>
    <p>Changing keys or secrets needs a sign-in from the last 5 minutes.</p>
    {!google && <label htmlFor="aw-confirm-password">Your admin password
      <input id="aw-confirm-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>}
    <ErrorNotice error={error} />
    <button className="aw-button aw-primary" disabled={busy}>{busy ? "Checking…" : google ? "Confirm with Google" : "Confirm sign-in"}</button>
  </form>;
}

// Runs a secret change: asks for a fresh sign-in first when needed, and again if the server says so.
function useFreshAction() {
  const [needSignIn, setNeedSignIn] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState(null), [notice, setNotice] = useState("");
  const keys = useRef({}), alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const run = async (name, call, done) => {
    if (busy) return;
    setError(null); setNotice("");
    if ((await adminSignInAge()) > FRESH_MS) { setNeedSignIn(true); return; }
    keys.current[name] = keys.current[name] || operationKey();
    setBusy(true);
    try {
      const result = await call(keys.current[name]);
      delete keys.current[name];
      if (alive.current) { setNotice(done(result)); setNeedSignIn(false); }
    } catch (e) {
      if (!alive.current) return;
      if (e.code === "reauthentication_required") setNeedSignIn(true);
      setError(e);
    } finally { if (alive.current) setBusy(false); }
  };
  return { needSignIn, setNeedSignIn, busy, error, notice, run };
}

function BookingSecret() {
  const [tick, setTick] = useState(0), state = useRemote("/admin/owner-settings/booking", tick);
  const [secret, setSecret] = useState(""), action = useFreshAction();
  const send = (webhookSecret) => action.run(webhookSecret === null ? "remove" : "save", (key) => adminRequest("/admin/owner-settings/booking", { method: "PUT", key,
    body: { revision: state.data.revision, value: state.data.value, webhookSecret } }), () => { setSecret(""); setTick((n) => n + 1);
    return webhookSecret === null ? "Webhook secret removed." : "Webhook secret saved. Only its last 4 characters are shown."; });
  return <section className="aw-panel" aria-labelledby="booking-secret-title">
    <h2 id="booking-secret-title">Cal.com webhook secret (optional)</h2>
    <p className="aw-muted">Lets the website record bookings made through your links. Paste the signing secret from the Cal.com webhook settings. Save the booking links first.</p>
    <Loading state={state}>{state.data && <>
      <p className="aw-notice">{state.data.webhookSecret ? `Saved · ends with …${state.data.webhookSecret.endsWith}` : "Not set."}</p>
      {action.needSignIn && <SignInAgain onDone={() => action.setNeedSignIn(false)} />}
      <ErrorNotice error={action.error} />{action.notice && <p className="aw-notice" role="status">{action.notice}</p>}
      {state.data.value ? <form onSubmit={(e) => { e.preventDefault(); send(secret.trim()); }}>
        <fieldset disabled={action.busy}>
          <label htmlFor="aw-booking-secret">New webhook secret
            <input id="aw-booking-secret" type="password" autoComplete="new-password" spellCheck={false} required minLength={16} maxLength={200}
              value={secret} onChange={(e) => setSecret(e.target.value)} /></label>
          <div className="aw-row"><button className="aw-button aw-primary">{action.busy ? "Saving…" : state.data.webhookSecret ? "Replace secret" : "Save secret"}</button>
            {state.data.webhookSecret && <button type="button" className="aw-button" onClick={() => send(null)}>Remove secret</button>}</div>
        </fieldset>
      </form> : <p className="aw-muted">Save the two booking links above first.</p>}
    </>}</Loading>
  </section>;
}

export function OwnerSettings() {
  return <>
    <SettingPanel id="company" title="Company profile" description="Printed on receipts, legal pages and emails." schema={COMPANY} copy={COMPANY_COPY} />
    <SettingPanel id="notifications" title="Owner alerts" description="New orders and payments are announced to these addresses (1 to 5)." schema={ALERTS} copy={ALERTS_COPY} />
    <SettingPanel id="booking" title="Booking links (Cal.com)" description="Standard and Premium buyers book their meetings through these links." schema={BOOKING} copy={BOOKING_COPY} />
    <BookingSecret />
  </>;
}

const LEGAL_SCHEMA = { type: "object", properties: { title: req(200), body: { ...req(100000) } }, required: ["title", "body"] };
export function LegalTexts() {
  const [tick, setTick] = useState(0), list = useRemote("/admin/legal", tick);
  const [kind, setKind] = useState(null), item = useRemote(kind ? `/admin/legal/${kind}` : null, tick), versions = useRemote(kind ? `/admin/legal/${kind}/versions` : null, tick);
  const [publishing, setPublishing] = useState(false), [error, setError] = useState(null), [notice, setNotice] = useState(""), key = useRef(null);
  const [dialog, ask] = useConfirm();
  const publish = async () => {
    if (publishing) return;
    if (!(await ask({ title: "Publish this text?", message: item.data.public ? "Buyers will see it, and this version can never be changed afterwards." : "This version can never be changed afterwards. It is not shown on the website.", confirm: "Publish" }))) return;
    key.current = key.current || operationKey(); setPublishing(true); setError(null); setNotice("");
    try {
      const r = await adminRequest(`/admin/legal/${kind}/publish`, { method: "POST", key: key.current, body: { revision: item.data.revision } });
      key.current = null; setNotice(`Published as version ${r.data.version}.`); setTick((n) => n + 1);
    } catch (e) { setError(e); } finally { setPublishing(false); }
  };
  return <section className="aw-panel" aria-labelledby="legal-title">
    {dialog}
    <h2 id="legal-title">Legal texts</h2>
    <p className="aw-muted">Write a draft, then publish it. Each publish becomes a new version that never changes, so every order keeps the exact text the buyer accepted. The NDA template is never shown on the website.</p>
    <Loading state={list}>{list.data && <ul className="aw-list">{list.data.items.map((x) => <li key={x.kind}>
      <button type="button" className={`aw-button${kind === x.kind ? " aw-primary" : ""}`} onClick={() => { setKind(x.kind); setNotice(""); setError(null); }}>{x.label}</button>{" "}
      <span className="aw-muted">{x.published ? `Version ${x.published} · ${when(x.publishedAt)}` : "Not published"}{x.draftChanged ? " · unpublished changes" : ""}{x.public ? "" : " · never public"}</span>
    </li>)}</ul>}</Loading>
    {kind && <Loading state={item}>{item.data && <>
      <ActionForm key={`${kind}:${item.data.revision}`} title={`${item.data.label}: draft`} schema={LEGAL_SCHEMA} button="Save draft"
        initial={{ title: item.data.draft?.title || item.data.published?.title || item.data.label, body: item.data.draft?.body || item.data.published?.body || "" }}
        submit={(body, k) => adminRequest(`/admin/legal/${kind}`, { method: "PUT", key: k, body: { revision: item.data.revision, ...body } })}
        onDone={() => setTick((n) => n + 1)} />
      <ErrorNotice error={error} />{notice && <p className="aw-notice" role="status">{notice}</p>}
      <div className="aw-row"><button type="button" className="aw-button aw-primary" disabled={publishing || !item.data.draft} onClick={publish}>{publishing ? "Publishing…" : "Publish saved draft"}</button></div>
      <Loading state={versions}>{versions.data?.items?.length ? <><h3>Published versions</h3><ul className="aw-list">{versions.data.items.map((v) =>
        <li key={v.version}>Version {v.version} · {when(v.publishedAt)}</li>)}</ul></> : null}</Loading>
    </>}</Loading>}
  </section>;
}

const MODE_NAME = { test: "Test", live: "Live" };
const CHECK = { ok: "OK", rejected: "Stripe rejected the secret key", unreachable: "Could not reach Stripe. Try again", not_checked: "Not tested yet" };
const KEYS_SCHEMA = { type: "object", properties: { publishableKey: req(300), secretKey: req(300), webhookSecret: req(300) }, required: ["publishableKey", "secretKey", "webhookSecret"] };
const KEYS_COPY = { publishableKey: { label: "Publishable key", help: "Starts with pk_test_ or pk_live_." },
  secretKey: { label: "Secret key", secret: true, help: "Starts with sk_ or rk_ (restricted key). It is never shown again." },
  webhookSecret: { label: "Webhook signing secret", secret: true, help: "Starts with whsec_. From the webhook you created in Stripe for this website." } };
function sourceText(data) {
  if (data.activeMode) return `Card payments use your ${MODE_NAME[data.activeMode].toLowerCase()} keys from this page.`;
  return data.source === "server" ? "Card payments use the keys set on the server. Paste and switch on keys here to use your own." : "Card payments are not set up.";
}
export function StripeConnection({ canManage }) {
  const [tick, setTick] = useState(0), state = useRemote("/admin/payments/stripe", tick);
  const [editing, setEditing] = useState(null), [draft, setDraft] = useState({}), [target, setTarget] = useState("");
  const action = useFreshAction(), [checkBusy, setCheckBusy] = useState(false), [checkError, setCheckError] = useState(null);
  const [dialog, ask] = useConfirm();
  const reload = () => setTick((n) => n + 1);
  const save = (mode) => action.run(`save-${mode}`, (key) => adminRequest(`/admin/payments/stripe/${mode}`, { method: "PUT", key,
    body: { revision: state.data.revision, ...draft } }), (r) => { setDraft({}); setEditing(null); reload();
    return `${MODE_NAME[mode]} keys saved (secret key ends with …${r.data.secretKeyEnding}). Press Test connection next.`; });
  const check = async (mode) => {
    setCheckBusy(true); setCheckError(null);
    try { await adminRequest(`/admin/payments/stripe/${mode}/check`, { method: "POST", body: { revision: state.data.revision } }); reload(); }
    catch (e) { setCheckError(e); } finally { setCheckBusy(false); }
  };
  const activate = async (mode) => {
    const next = mode === "" ? null : mode;
    if (next === "live" && !(await ask({ title: "Switch to live keys?", message: "From now on card payments take real money.", confirm: "Switch to live", destructive: true }))) return;
    action.run(`activate-${mode}`, (key) => adminRequest("/admin/payments/stripe/active", { method: "PUT", key, body: { revision: state.data.revision, mode: next } }),
      () => { reload(); return next ? `${MODE_NAME[next]} keys are now used for card payments.` : "Card payments are back on the server keys."; });
  };
  return <section className="aw-panel" aria-labelledby="stripe-title">
    {dialog}
    <h2 id="stripe-title">Card payments (Stripe)</h2>
    <Loading state={state}>{state.data && <>
      <p className="aw-notice" role="status">{sourceText(state.data)}</p>
      {!state.data.serverKeyAvailable && <p className="aw-notice aw-error">Secret settings are switched off on this server, so keys cannot be saved or used here yet.</p>}
      {canManage && action.needSignIn && <SignInAgain onDone={() => action.setNeedSignIn(false)} />}
      <ErrorNotice error={action.error || checkError} />{action.notice && <p className="aw-notice" role="status">{action.notice}</p>}
      {["test", "live"].map((mode) => { const m = state.data.modes[mode]; return <div key={mode} className="aw-panel">
        <h3>{MODE_NAME[mode]} keys{state.data.activeMode === mode ? " · in use" : ""}</h3>
        {m.connected ? <p>Connected · secret key ends with …{m.secretKeyEnding} · webhook secret ends with …{m.webhookSecretEnding}
          {m.accountId ? ` · Stripe account ${m.accountId}` : ""} · Test connection: {CHECK[m.checkResult] || m.checkResult}{m.checkedAt ? ` (${when(m.checkedAt)})` : ""}</p>
          : <p className="aw-muted">Not connected.</p>}
        {canManage && <div className="aw-row">
          <button type="button" className="aw-button" onClick={() => { setEditing(editing === mode ? null : mode); setDraft({}); }}>{m.connected ? "Replace keys" : "Paste keys"}</button>
          {m.connected && <button type="button" className="aw-button" disabled={checkBusy} onClick={() => check(mode)}>{checkBusy ? "Testing…" : "Test connection"}</button>}
        </div>}
        {canManage && editing === mode && <form onSubmit={(e) => { e.preventDefault(); save(mode); }}>
          <fieldset disabled={action.busy}><Fields schema={KEYS_SCHEMA} value={draft} onChange={setDraft} prefix={`stripe-${mode}`} fieldCopy={KEYS_COPY} />
            <button className="aw-button aw-primary">{action.busy ? "Saving…" : `Save ${MODE_NAME[mode].toLowerCase()} keys`}</button></fieldset>
        </form>}
      </div>; })}
      {state.data.webhook && <p className="aw-muted">Last signed payment message from Stripe: {when(state.data.webhook.lastVerifiedAt)} ({state.data.webhook.mode} mode).</p>}
      {canManage && <form className="aw-row" onSubmit={(e) => { e.preventDefault(); activate(target); }}>
        <label htmlFor="aw-stripe-active">Use for card payments
          <select id="aw-stripe-active" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">The server keys</option>
            <option value="test" disabled={!state.data.modes.test.ready || state.data.testModeAllowed === false}>Test keys from this page</option>
            <option value="live" disabled={!state.data.modes.live.ready}>Live keys from this page</option>
          </select></label>
        <button className="aw-button aw-primary" disabled={action.busy}>Switch</button>
      </form>}
    </>}</Loading>
  </section>;
}

// One line for the Orders page (owner and Accountant): which Stripe keys take card payments.
export function StripeStatusLine() {
  const state = useRemote("/admin/payments/stripe");
  if (!state.data) return null;
  const m = state.data.activeMode ? state.data.modes[state.data.activeMode] : null;
  return <p className="aw-muted" role="status">{sourceText(state.data)}{m?.connected ? ` Secret key ends with …${m.secretKeyEnding}.` : ""}</p>;
}
